import type { SpinResponse } from "@slot/client-engine";
import { fx, spinFixture } from "@slot/game-shell";
import {
  BATTLE_VALUES,
  BUY_REVEAL_BASE_GRID,
  GRID_HEIGHT,
  GRID_WIDTH,
  MAX_WIN_CAP,
  MODE_REEL_SETS,
  PAYTABLE,
  SAVED_TOTAL_CAP,
  SCATTER,
  SESSIONS,
  VS,
  WILD,
  anticipatedColumns,
  bigWinTier,
  evaluatePaylines,
  groupWins,
  positionMask,
  scatterCells,
  vsColumns,
  winRatioLabel,
  type SessionId,
} from "./teddyRules";

/** One VS battle as the server drew it for a column: its two values (the top strictly smaller) and which side won. */
export interface BattleScript {
  readonly column: number;
  readonly top: number;
  readonly bottom: number;
  readonly winner: "top" | "bottom";
}

/** One spin as the server landed it: the grid (rows, top first) and a battle for every column holding a VS. */
export interface SpinScript {
  readonly grid: readonly (readonly string[])[];
  readonly battles?: readonly BattleScript[];
}

/** A natural round: its base spin and, when that spin triggers, the session's free spins (every one it plays). */
export interface NaturalRoundScript {
  readonly id: string;
  readonly betMinor?: bigint;
  /** The feature mode the spin was bought under (its reel set); none for a plain base spin. */
  readonly mode?: "bonus_hunt_mode" | "guaranteed_vs_mode";
  readonly base: SpinScript;
  readonly freeSpins?: readonly SpinScript[];
}

/** A bought round: the session's synthetic reveal (its scatters' cells, at most one per reel) and its free spins. */
export interface BuyRoundScript {
  readonly id: string;
  readonly betMinor?: bigint;
  readonly session: SessionId;
  readonly scatterCells: readonly number[];
  readonly freeSpins: readonly SpinScript[];
}

/** The shell DSL's event builder (the argument of `spinFixture(...).event`'s callback). */
type EventBuilder = Parameters<Parameters<ReturnType<typeof spinFixture>["event"]>[2]>[0];
/** One `fx` action: its type, its phase, its payload. */
type FixtureAction = { readonly type: string; readonly phase: ReturnType<typeof fx.spinBoundaryStart>["phase"]; readonly payload: Record<string, unknown> };

/** The offline account the rounds report (the server reports the balance after the bet, before the win). */
const OFFLINE_BALANCE = { currencyCode: "EUR", balanceMinor: "100000" } as const;
/** The live backend's response schema. */
const SCHEMA_VERSION = "3";
const DEFAULT_BET_MINOR = 100n;

const EXPANDING_WILD = "expanding_wild";

interface StepResult {
  readonly grid: readonly (readonly string[])[];
  readonly presented: readonly BattleScript[];
  readonly groups: ReturnType<typeof groupWins>;
  readonly spinWinMinor: bigint;
  readonly scatters: readonly number[];
}

/**
 * Builds the offline rounds in the backend's exact shape, by its rules (`teddyRules.ts` mirrors them): the base spin's
 * `reveal` (its presented battles, its grouped line wins, the meter, the big win, the trigger), then `feature_enter`,
 * the free spins (`fs_reveal`, with the Super Bonus memory's add and collect, the retriggers and the cap) and
 * `feature_exit`. A script the server could not produce throws: a battle off the ladder or unordered, a missing or
 * extra battle, a Super Bonus top that is not the saved total, too few or too many free spins.
 */
export function buildNaturalRound(script: NaturalRoundScript): SpinResponse {
  const bet = script.betMinor ?? DEFAULT_BET_MINOR;
  const cap = bet * BigInt(MAX_WIN_CAP);
  const step = runStep(script.base, bet, { floor: undefined, where: `${script.id} base` });
  const session = step.spinWinMinor >= cap ? undefined : triggeredSession(step.scatters.length);
  const builder = spinFixture(script.id).schema(SCHEMA_VERSION).balance(OFFLINE_BALANCE.currencyCode, OFFLINE_BALANCE.balanceMinor);
  builder.event("reveal", 0, (e) => {
    startSpinEvent(e, step, { bet, reelSet: script.mode ? MODE_REEL_SETS[script.mode]! : "default", boundary: "base:1", mode: "base", displayWinMinor: step.spinWinMinor });
    appendBigWin(e, step.spinWinMinor, bet);
    if (session) appendTrigger(e, step.scatters, SESSIONS[session].featureId, SESSIONS[session].awardedSpins);
    action(e, fx.spinBoundaryEnd("base:1", step.spinWinMinor.toString()));
    const teased = anticipatedColumns(step.scatters, Math.min(SESSIONS.vsRush.triggerScatterCount, SESSIONS.superBonus.triggerScatterCount));
    if (teased.length > 0) e.anticipate(teased);
  });
  if (!session) {
    if (script.freeSpins?.length) throw new Error(`${script.id}: the base spin triggers no session, yet ${script.freeSpins.length} free spins are scripted`);
    return builder.build();
  }
  appendSession(builder, script.id, session, step.spinWinMinor, bet, script.freeSpins ?? []);
  return builder.build();
}

export function buildBuyRound(script: BuyRoundScript): SpinResponse {
  const bet = script.betMinor ?? DEFAULT_BET_MINOR;
  const session = SESSIONS[script.session];
  const columns = script.scatterCells.map((cell) => cell % GRID_WIDTH);
  if (script.scatterCells.length !== session.triggerScatterCount || new Set(columns).size !== columns.length) {
    throw new Error(`${script.id}: a ${session.featureId} reveal lands exactly ${session.triggerScatterCount} scatters, at most one per reel`);
  }
  const grid = BUY_REVEAL_BASE_GRID.map((row) => [...row]);
  for (const cell of script.scatterCells) grid[Math.floor(cell / GRID_WIDTH)]![cell % GRID_WIDTH] = SCATTER;
  const scatters = scatterCells(grid);
  const builder = spinFixture(script.id).schema(SCHEMA_VERSION).balance(OFFLINE_BALANCE.currencyCode, OFFLINE_BALANCE.balanceMinor);
  builder.event("reveal", 0, (e) => {
    e.reelSet("default").gridRows(grid).betAmount(bet.toString()).accumulatedWinAfter("0");
    action(e, fx.spinBoundaryStart("base:1", "base"));
    action(e, fx.updateTotalWinMeter("0"));
    appendTrigger(e, scatters, session.featureId, session.awardedSpins);
    action(e, fx.spinBoundaryEnd("base:1", "0"));
    const teased = anticipatedColumns(scatters, session.triggerScatterCount);
    if (teased.length > 0) e.anticipate(teased);
  });
  appendSession(builder, script.id, script.session, 0n, bet, script.freeSpins);
  return builder.build();
}

/** `feature_enter`, the free spins, `feature_exit`: the server's session loop. */
function appendSession(builder: ReturnType<typeof spinFixture>, id: string, sessionId: SessionId, triggerWinMinor: bigint, bet: bigint, spins: readonly SpinScript[]): void {
  const session = SESSIONS[sessionId];
  const superBonus = sessionId === "superBonus";
  const cap = bet * BigInt(MAX_WIN_CAP);
  let total = triggerWinMinor;
  builder.event("feature_enter", 1, (e) => {
    e.betAmount(bet.toString()).featureStateSet({ featureIdentifier: session.featureId, featureCountRemaining: session.awardedSpins.toString(), featureAccumulatedWinMinor: total.toString(), featurePhase: "enter" });
    action(e, fx.updateTotalWinMeter(total.toString()));
  });
  let remaining: number = session.awardedSpins;
  let savedTotal: number | undefined;
  let index = 0;
  while (index < remaining) {
    const spin = spins[index];
    index += 1;
    if (!spin) throw new Error(`${id}: the session plays ${remaining} free spins, only ${spins.length} are scripted`);
    const step = runStep(spin, bet, { floor: superBonus ? savedTotal : undefined, where: `${id} free spin ${index}` });
    const adds: { increment: number; total: number }[] = [];
    if (superBonus && step.presented.length > 0) {
      const best = Math.max(...step.presented.map(winnerValue));
      const previous = savedTotal ?? 0;
      const next = Math.min(Math.max(previous, best), SAVED_TOTAL_CAP);
      if (next > previous) {
        adds.push({ increment: next - previous, total: next });
        savedTotal = next;
      }
    }
    const before = total;
    const capped = before + step.spinWinMinor >= cap;
    total = capped ? cap : before + step.spinWinMinor;
    const display = capped ? cap - before : step.spinWinMinor;
    const retriggered = !capped && step.scatters.length >= session.retriggerScatterCount && remaining + session.retriggerAwardedSpins <= session.maxSpinsSafety;
    const last = capped || (!retriggered && index >= remaining);
    const boundary = `free_spins:${index}`;
    const remainingAfter = capped ? 0 : remaining - index;
    const totalAfter = total;
    const collect = superBonus && last && savedTotal !== undefined ? savedTotal : undefined;
    builder.event("fs_reveal", index + 1, (e) => {
      startSpinEvent(e, step, {
        bet,
        reelSet: session.reelSetIdentifier,
        boundary,
        mode: "free_spins",
        displayWinMinor: display,
        beforeMeter: () => {
          for (const add of adds) action(e, fx.addFeatureMultiplier({ multiplierAdded: add.increment.toString(), totalMultiplier: add.total.toString() }));
          if (collect !== undefined) action(e, fx.collectFeatureMultiplier({ multiplierAdded: collect.toString(), collectedSum: collect.toString() }));
        },
      });
      appendBigWin(e, display, bet);
      if (retriggered) appendTrigger(e, step.scatters, session.featureId, session.retriggerAwardedSpins);
      action(e, fx.spinBoundaryEnd(boundary, display.toString()));
      e.featureStateSet({ featureIdentifier: session.featureId, featureCountRemaining: remainingAfter.toString(), featureAccumulatedWinMinor: totalAfter.toString(), featurePhase: "update" });
      const teased = anticipatedColumns(step.scatters, session.retriggerScatterCount);
      if (teased.length > 0) e.anticipate(teased);
    });
    if (capped) break;
    if (retriggered) remaining += session.retriggerAwardedSpins;
  }
  if (spins.length > index) throw new Error(`${id}: the session plays ${index} free spins, ${spins.length} are scripted`);
  builder.event("feature_exit", index + 2, (e) => {
    e.betAmount(bet.toString()).accumulatedWinAfter(total.toString()).featureStateSet({ featureIdentifier: session.featureId, featureCountRemaining: "0", featureAccumulatedWinMinor: total.toString(), featurePhase: "exit" });
  });
}

/** One spin by the rules: its battles checked, its lines evaluated with every battle's column wild, its win capped. */
function runStep(spin: SpinScript, bet: bigint, context: { floor: number | undefined; where: string }): StepResult {
  const { grid } = spin;
  if (grid.length !== GRID_HEIGHT || grid.some((row) => row.length !== GRID_WIDTH)) throw new Error(`${context.where}: the grid is not ${GRID_WIDTH}×${GRID_HEIGHT}`);
  for (const cell of grid.flat()) {
    if (!PAYTABLE[cell] && cell !== WILD && cell !== VS && cell !== SCATTER) throw new Error(`${context.where}: "${cell}" is no symbol of this game`);
  }
  const battles = [...(spin.battles ?? [])].sort((a, b) => a.column - b.column);
  const columns = vsColumns(grid);
  if (battles.map((b) => b.column).join() !== columns.join()) throw new Error(`${context.where}: the VS columns are [${columns}], the battles are on [${battles.map((b) => b.column)}]`);
  for (const battle of battles) {
    if (!BATTLE_VALUES.includes(battle.top) || !BATTLE_VALUES.includes(battle.bottom) || battle.bottom <= battle.top) {
      throw new Error(`${context.where}: the battle on column ${battle.column} is not an ordered pair on the ladder (${battle.top} / ${battle.bottom})`);
    }
    if (context.floor !== undefined && battle.top !== context.floor) {
      throw new Error(`${context.where}: a Super Bonus battle opens on the saved total ${context.floor}, not ${battle.top}`);
    }
  }
  const expanded = new Map(battles.map((b) => [b.column, winnerValue(b)]));
  const wins = evaluatePaylines(grid, expanded);
  const crossed = new Set(wins.flatMap((w) => w.cells.map((cell) => cell % GRID_WIDTH)));
  const groups = groupWins(wins, bet);
  const raw = groups.reduce((sum, g) => sum + g.totalMinor, 0n);
  const cap = bet * BigInt(MAX_WIN_CAP);
  return { grid, presented: battles.filter((b) => crossed.has(b.column)), groups, spinWinMinor: raw > cap ? cap : raw, scatters: scatterCells(grid) };
}

/** The spin event through its meter: the boundary, the battles, the grouped highlights, the title's slot, the meter. */
function startSpinEvent(
  e: EventBuilder,
  step: StepResult,
  input: { bet: bigint; reelSet: string; boundary: string; mode: string; displayWinMinor: bigint; beforeMeter?: () => void },
): void {
  e.reelSet(input.reelSet).gridRows(step.grid.map((row) => [...row])).betAmount(input.bet.toString()).accumulatedWinAfter(input.displayWinMinor.toString());
  action(e, fx.spinBoundaryStart(input.boundary, input.mode));
  for (const battle of step.presented) {
    e.action(
      EXPANDING_WILD,
      { positionColumn: battle.column.toString(), wildSymbolIdentifier: VS, multipliers: [battle.top.toString(), battle.bottom.toString()], winningIndex: battle.winner === "top" ? "0" : "1" },
      { actionPhaseName: "win" },
    );
  }
  for (const group of step.groups) {
    action(
      e,
      fx.gridWinHighlight({
        positionMask: positionMask(group.cells),
        winAmount: group.totalMinor.toString(),
        symbolIdentifier: group.symbolId,
        symbolCount: group.count.toString(),
        lines: group.members.map((m) => ({
          positionMask: positionMask(m.cells),
          winAmount: m.winMinor.toString(),
          symbolCount: m.count.toString(),
          baseWinAmount: m.baseWinMinor.toString(),
          multiplierApplied: m.multiplierApplied.toString(),
        })),
      }),
    );
  }
  input.beforeMeter?.();
  action(e, fx.updateTotalWinMeter(input.displayWinMinor.toString()));
}

function appendBigWin(e: EventBuilder, winMinor: bigint, bet: bigint): void {
  const tier = bigWinTier(winMinor, bet);
  if (tier) action(e, fx.presentBigWin({ levelIdentifier: tier, spinWinAmount: winMinor.toString(), betAmount: bet.toString(), winRatioLabel: winRatioLabel(winMinor, bet) }));
}

function appendTrigger(e: EventBuilder, scatters: readonly number[], featureId: string, awarded: number): void {
  action(e, fx.presentScatterState({ positionMask: positionMask(scatters), scatterCount: scatters.length.toString() }));
  action(e, fx.bonusFeatureWon(featureId, awarded.toString()));
}

/** The session the landed scatters trigger: the highest trigger count they reach. */
function triggeredSession(scatterCount: number): SessionId | undefined {
  if (scatterCount >= SESSIONS.superBonus.triggerScatterCount) return "superBonus";
  if (scatterCount >= SESSIONS.vsRush.triggerScatterCount) return "vsRush";
  return undefined;
}

function winnerValue(battle: BattleScript): number {
  return battle.winner === "top" ? battle.top : battle.bottom;
}

function action(e: EventBuilder, a: FixtureAction): void {
  e.action(a.type, a.payload, { actionPhaseName: a.phase });
}
